import type { MaskInputOptions, MaskTokens } from 'maska';

type CaseMode = 'none' | 'upper' | 'lower';

const LETTER_PATTERN = /\p{L}/u;
const ALNUM_PATTERN = /[\p{L}0-9]/u;
const ANY_PATTERN = /./;
const DIGIT_PATTERN = /[0-9]/;
const HASH_PATTERN = /[0-9+-]/;
const CASE_TOKEN_CODE_POINT_START = 0xe000;
const DEFAULT_PLACEHOLDER_CHAR = '_';

const INPUT_MASK_TOKENS: MaskTokens = {
  'L': { pattern: LETTER_PATTERN },
  'l': { pattern: LETTER_PATTERN, optional: true },
  'A': { pattern: ALNUM_PATTERN },
  'a': { pattern: ALNUM_PATTERN, optional: true },
  'C': { pattern: ANY_PATTERN },
  'c': { pattern: ANY_PATTERN, optional: true },
  '0': { pattern: DIGIT_PATTERN },
  '9': { pattern: DIGIT_PATTERN, optional: true },
  '#': { pattern: HASH_PATTERN, optional: true },
};

const PLACEHOLDER_CHARS = new Set(Object.keys(INPUT_MASK_TOKENS));
const DEFAULT_MASKA_SLOT_CHARS = new Set([...PLACEHOLDER_CHARS, '*', '@']);
const LITERAL_ESCAPE_CHARS = new Set([...PLACEHOLDER_CHARS, '*', '@', '!']);

export type InputMaskOptions = MaskInputOptions & {
  placeholderChar?: string;
  readableMask?: string;
};

function mapCase(char: string, caseMode: Exclude<CaseMode, 'none'>) {
  const mapped = caseMode === 'upper' ? char.toLocaleUpperCase() : char.toLocaleLowerCase();

  return mapped.length === 1 ? mapped : char;
}

function maskValueLength(mask: string) {
  let length = 0;

  for (let index = 0; index < mask.length; index++) {
    if (mask[index] === '!' && index + 1 < mask.length) {
      length++;
      index++;
      continue;
    }

    length++;
  }

  return length;
}

function parseEditMask(mask: string) {
  let pattern = '';
  let placeholderChar = '';
  let field = 1;

  for (let index = 0; index < mask.length; index++) {
    const char = mask[index];

    if (char === '\\' && index + 1 < mask.length) {
      const escaped = mask[index + 1];

      if (field === 1) {
        pattern += `\\${escaped}`;
      } else if (field === 3 && !placeholderChar) {
        placeholderChar = escaped;
      }

      index++;
      continue;
    }

    if (char === ';' && field < 3) {
      field++;
      continue;
    }

    if (field === 1) {
      pattern += char;
      continue;
    }

    if (field === 3 && !placeholderChar) {
      placeholderChar = char;
    }
  }

  return {
    pattern,
    placeholderChar: placeholderChar || DEFAULT_PLACEHOLDER_CHAR,
  };
}

export function toInputMask(mask: string | null | undefined): InputMaskOptions | undefined {
  if (!mask) {
    return undefined;
  }

  const { pattern: source, placeholderChar } = parseEditMask(mask);
  const tokens: MaskTokens = { ...INPUT_MASK_TOKENS };
  const caseTokenKeys = new Map<string, string>();
  let nextCaseTokenCode = CASE_TOKEN_CODE_POINT_START;
  let result = '';
  let hasPlaceholder = false;
  let caseMode: CaseMode = 'none';

  const appendLiteral = (char: string) => {
    if (LITERAL_ESCAPE_CHARS.has(char) || tokens[char]) {
      result += `!${char}`;
      return;
    }

    result += char;
  };

  const appendPlaceholder = (char: string) => {
    if (caseMode === 'none') {
      result += char;
      return;
    }

    const activeCaseMode = caseMode;
    const cacheKey = `${activeCaseMode}:${char}`;
    let key = caseTokenKeys.get(cacheKey);

    if (!key) {
      key = String.fromCodePoint(nextCaseTokenCode++);
      caseTokenKeys.set(cacheKey, key);
      tokens[key] = {
        ...INPUT_MASK_TOKENS[char],
        transform: (value: string) => mapCase(value, activeCaseMode),
      };
    }

    result += key;
  };

  for (let index = 0; index < source.length; index++) {
    const char = source[index];

    if (char === '\\' && index + 1 < source.length) {
      appendLiteral(source[index + 1]);
      index++;
      continue;
    }

    if (char === '<' && source[index + 1] === '>') {
      caseMode = 'none';
      index++;
      continue;
    }

    if (char === '>') {
      caseMode = 'upper';
      continue;
    }

    if (char === '<') {
      caseMode = 'lower';
      continue;
    }

    if (PLACEHOLDER_CHARS.has(char)) {
      hasPlaceholder = true;
      appendPlaceholder(char);
      continue;
    }

    appendLiteral(char);
  }

  if (!hasPlaceholder) {
    return undefined;
  }

  return {
    mask: result,
    tokens,
    tokensReplace: true,
    eager: true,
    placeholderChar,
    readableMask: toReadableMask(mask),
  };
}

function resolveBlankChar(placeholderChar?: string) {
  if (!placeholderChar || placeholderChar === DEFAULT_PLACEHOLDER_CHAR) {
    return ' ';
  }

  return placeholderChar;
}

export function toReadableMask(mask: string | null | undefined): string | undefined {
  if (!mask) {
    return undefined;
  }

  const { pattern: source, placeholderChar } = parseEditMask(mask);
  const blank = resolveBlankChar(placeholderChar);
  let result = '';
  let hasPlaceholder = false;

  for (let index = 0; index < source.length; index++) {
    const char = source[index];

    if (char === '\\' && index + 1 < source.length) {
      result += source[index + 1];
      index++;
      continue;
    }

    if (char === '<' && source[index + 1] === '>') {
      index++;
      continue;
    }

    if (char === '>' || char === '<') {
      continue;
    }

    if (PLACEHOLDER_CHARS.has(char)) {
      hasPlaceholder = true;
      result += blank;
      continue;
    }

    result += char;
  }

  return hasPlaceholder ? result : undefined;
}

function readableFromMaskaPattern(mask: InputMaskOptions) {
  if (typeof mask.mask !== 'string') {
    return undefined;
  }

  const blank = resolveBlankChar(mask.placeholderChar);
  const tokens = mask.tokens ?? {};
  const explicitTokens = mask.tokens != null;
  let result = '';
  let hasPlaceholder = false;

  for (let index = 0; index < mask.mask.length; index++) {
    const char = mask.mask[index];

    if (char === '!' && index + 1 < mask.mask.length) {
      result += mask.mask[index + 1];
      index++;
      continue;
    }

    const isSlot = tokens[char] != null || (!explicitTokens && DEFAULT_MASKA_SLOT_CHARS.has(char));

    if (isSlot) {
      hasPlaceholder = true;
      result += blank;
      continue;
    }

    result += char;
  }

  return hasPlaceholder ? result : undefined;
}

function normalizeReadablePlaceholder(value: string | undefined) {
  if (!value?.trim()) {
    return undefined;
  }

  return value;
}

export function getReadableMaskPlaceholder(mask: InputMaskOptions | string | null | undefined): string | undefined {
  if (!mask) {
    return undefined;
  }

  if (typeof mask === 'string') {
    return normalizeReadablePlaceholder(toReadableMask(mask) ?? readableFromMaskaPattern({ mask }));
  }

  if (mask.readableMask) {
    return normalizeReadablePlaceholder(mask.readableMask);
  }

  return normalizeReadablePlaceholder(readableFromMaskaPattern(mask));
}

export function getInputMaskMaxLength(mask: MaskInputOptions | undefined, fallback: number) {
  if (typeof mask?.mask !== 'string') {
    return fallback;
  }

  return Math.max(fallback, maskValueLength(mask.mask));
}
